// IFrameViewer + InnerViewer 가 공통으로 사용하는 helper module.
//
// 본 트랙 F P5 A1 (viewer-refactor-task.md §2 A1) 의 단계적 helper 추출 — explore agent
// 의 line-by-line diff 분석에서 100% identical 로 확정된 16 메서드를 그룹별로 추출.
//
// PoC (commit 1dacff4b2): attachReactMgr / detachReactMgr 2 메서드.
// Group 1 (commit 78ec8c79c): invitation 5 메서드 (extractInviteAddressList /
//   onCreateInvitation / handleCreateEvent / handleShowCalendar / handleCopyInvitation).
//   2 메서드 (handleInvitationMenuClick / handleInvitationMenuVisibleChange) 는
//   state/setState 의존으로 instance method 보존.
// Group 2: 변경 없음 — closeLoadingAni (4줄) / setNormalLoading (4줄) / applyThread (5줄)
//   모두 instance setState wrapper. 본체가 너무 단순해 helper 추출 시 추상화 비용 >
//   중복 제거 이득.
// Group 3 (본 commit): getJMAPMessageId 1 메서드 (pure utility). createEmailInfo (10줄)
//   는 emailInfoMap mutate + emailHelper 호출이라 wrapper 가 더 복잡해져 보존.
//   componentDidUpdate (28줄) 는 5 instance method 호출 lifecycle 이라 helper 부적합.
//
// "거의 동일" 메서드 (componentDidMount / componentWillUnmount / onEmailChanged /
// createRefObj 등) 는 통합 보류 — 모드별 다른 동작 또는 strategy injection 필요.

// email / emails 의 reactMgr shape 만 좁힘. ThreadViewProps 의 email/emails 가 any 라
// 호출자에서 type cast 없이 전달 가능.
interface EmailWithReactMgr {
  reactMgr?: {
    addReactObj: (obj: unknown) => void
    removeReactObj: (obj: unknown) => void
  }
}

interface ReactMgrTargetProps {
  email?: EmailWithReactMgr | null
  emails?: EmailWithReactMgr[] | null
}

/**
 * 양 클래스 (IFrameViewer / InnerViewer) 의 attachReactMgr 메서드의 단일 source.
 * lifecycle 의 componentDidMount / componentDidUpdate 에서 호출 — props 의 email (단일)
 * 또는 emails (배열) 의 reactMgr 에 자기 자신을 등록.
 */
export function attachReactMgr(props: ReactMgrTargetProps, reactObj: unknown): void {
  const { email, emails } = props
  if (email != null && email.reactMgr != null) {
    email.reactMgr.addReactObj(reactObj)
  } else if (emails != null) {
    emails.forEach((item) => {
      if (item.reactMgr != null) {
        item.reactMgr.addReactObj(reactObj)
      }
    })
  }
}

/**
 * detachReactMgr 의 단일 source. componentDidUpdate (prev props) / componentWillUnmount
 * 에서 호출 — 이전 등록을 제거.
 */
export function detachReactMgr(props: ReactMgrTargetProps, reactObj: unknown): void {
  const { email, emails } = props
  if (email != null && email.reactMgr != null) {
    email.reactMgr.removeReactObj(reactObj)
  } else if (emails != null) {
    emails.forEach((item) => {
      if (item.reactMgr != null) {
        item.reactMgr.removeReactObj(reactObj)
      }
    })
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Invitation 그룹 — Group 1 (commit <this>)
// ─────────────────────────────────────────────────────────────────────────────

interface EmailAddressInput {
  email?: string | null
  name?: string | null
}

interface InviteAddressEntry {
  name: string
  email: string
}

interface InviteAddressLists {
  required: InviteAddressEntry[]
  optional: InviteAddressEntry[]
}

/**
 * curEmail 의 from / to / cc 주소 목록에서 account 자기 자신을 제외한 attendee 분류.
 * from + to → required (참석 필수), cc → optional (선택).
 *
 * 양 클래스 (IFrameViewer L267-313 / InnerViewer L304-350) 의 100% identical 메서드의
 * 단일 source. pure function — instance state / setState 의존 없음.
 */
export function extractInviteAddressList(
  accountEmail: string,
  from: EmailAddressInput[],
  to: EmailAddressInput[],
  cc: EmailAddressInput[],
): InviteAddressLists {
  const required: Record<string, InviteAddressEntry> = {}
  const optional: Record<string, InviteAddressEntry> = {}

  if (!accountEmail) {
    return { required: [], optional: [] }
  }

  const normalizedAccountEmail = accountEmail.toLowerCase()

  const matchesAccount = (address: EmailAddressInput | null | undefined): boolean => {
    return address?.email?.toLowerCase() === normalizedAccountEmail
  }

  if (from != null && Array.isArray(from)) {
    from.forEach((addr) => {
      if (addr != null && !matchesAccount(addr)) {
        const e = addr.email ?? ''
        required[e] = { name: addr.name ?? '', email: e }
      }
    })
  }

  if (to != null && Array.isArray(to)) {
    to.forEach((addr) => {
      if (addr != null && !matchesAccount(addr)) {
        const e = addr.email ?? ''
        required[e] = { name: addr.name ?? '', email: e }
      }
    })
  }

  if (cc != null && Array.isArray(cc)) {
    cc.forEach((addr) => {
      if (addr != null && !matchesAccount(addr)) {
        const e = addr.email ?? ''
        optional[e] = { name: addr.name ?? '', email: e }
      }
    })
  }

  return { required: Object.values(required), optional: Object.values(optional) }
}

interface InvitationData {
  startDateCalendar?: unknown
  endDateCalendar?: unknown
  displayStr?: string
}

/**
 * 'createEvent' 분기 — 빈 attendee 로 modalEventEditor 호출 (현재 placeholder).
 * 양 클래스 100% identical pure function. invitationData 인자만 사용, 부수효과는
 * modalEventEditor 호출.
 */
export function handleCreateEvent(
  modalEventEditor: (
    start: unknown,
    end: unknown,
    subject: string,
    required: unknown,
    optional: unknown,
  ) => void,
  invitationData: InvitationData,
): void {
  const { startDateCalendar, endDateCalendar } = invitationData
  console.log('Create event:', startDateCalendar, endDateCalendar)
  modalEventEditor(startDateCalendar, endDateCalendar, 'test 1', null, null)
}

/**
 * 'showCalendar' 분기 — startDate 를 UTC midnight 으로 변환 후 /calendar 페이지로 이동.
 * window.location.href 부수효과.
 */
export function handleShowCalendar(
  momentFn: (input: unknown) => { format: (fmt: string) => string },
  invitationData: InvitationData,
): void {
  const { startDateCalendar } = invitationData
  console.log('Show calendar:', startDateCalendar)
  window.location.href =
    '/calendar?date=' + (momentFn(startDateCalendar).format('YYYYMMDDT000000[Z]') || '')
}

/**
 * 'copy' 분기 — invitationData.displayStr 을 clipboard 에 복사 + i18n 메시지.
 */
export function handleCopyInvitation(
  message: { success: (text: string) => void; error: (text: string) => void },
  t: (key: string) => string,
  invitationData: InvitationData,
): void {
  const { displayStr } = invitationData
  if (displayStr == null) return
  navigator.clipboard
    .writeText(displayStr)
    .then(() => {
      message.success(t('common.result.clipboardCopied'))
    })
    .catch((err: unknown) => {
      console.error('Failed to copy:', err)
      message.error(t('common.result.copyFailed'))
    })
}

interface CurEmailData {
  from?: EmailAddressInput[]
  to?: EmailAddressInput[]
  cc?: EmailAddressInput[]
  subject?: string
}

interface OnCreateInvitationProps {
  user?: { email?: string } | null
  curEmail?: { data?: CurEmailData } | null
}

/**
 * native 'createInvitation' callback. host i18n + modalEventEditor + props 의존을
 * 인자로 받아 helper 가 단일 source 로 처리. extractInviteAddressList 의 호출도
 * 본 helper 내부에서 처리.
 */
export function onCreateInvitation(
  outlookToCalendar: (date: string) => unknown,
  modalEventEditor: (
    start: unknown,
    end: unknown,
    subject: string,
    required: unknown,
    optional: unknown,
  ) => void,
  props: OnCreateInvitationProps,
  startDate: string,
  endDate: string,
): void {
  console.log(' --- onNotifyCreateInvitation : ', startDate, endDate)
  const startDateCalendar = outlookToCalendar(startDate)
  const endDateCalendar = outlookToCalendar(endDate)
  console.log(' --- onNotifyCreateInvitation date : ', startDateCalendar, endDateCalendar)

  const accountEmail = props?.user?.email ?? ''
  const from = props?.curEmail?.data?.from ?? []
  const to = props?.curEmail?.data?.to ?? []
  const cc = props?.curEmail?.data?.cc ?? []

  const { required, optional } = extractInviteAddressList(accountEmail, from, to, cc)

  modalEventEditor(
    startDateCalendar,
    endDateCalendar,
    props?.curEmail?.data?.subject ?? '',
    required,
    optional,
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Data 그룹 — Group 3 (commit <this>)
// ─────────────────────────────────────────────────────────────────────────────

interface MessageIdHeader {
  name: string
  value: string
}

interface EmailWithMessageIdHeaders {
  data: {
    headers: MessageIdHeader[]
  }
}

/**
 * email.data.headers 중 'message-id' (대소문자 무관) 헤더를 찾아 surrounding `<...>` 를
 * 제거한 순수 message-id 반환. JMAP 트레이드인 RFC 5322 의 angle bracket 포맷을 normalize.
 *
 * 양 클래스 (IFrameViewer L443-452 / InnerViewer L463-472) 의 100% identical pure
 * function — 인자만 사용, instance state 의존 없음.
 */
export function getJMAPMessageId(email: EmailWithMessageIdHeaders): string {
  const messageIdHeader = email.data.headers.find(
    (header) => header.name.toLowerCase() === 'message-id',
  )
  // 원본 동작 보존 — header 미발견 시 undefined.value 접근으로 TypeError throw.
  return messageIdHeader!.value.replace(/(^\s*<)|(>\s*$)/g, '')
}
